import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  EquitySimulatorGrowthChartProps,
  GrowthBarPoint,
} from "@/types/tradingTool";
import { cn } from "@/utils/utils";

function formatAxisTick(value: number) {
  if (value >= 1000) {
    return `${Math.round(value / 1000)}k`;
  }
  return value.toLocaleString("en-US");
}

function buildYAxis(maxValue: number) {
  if (maxValue <= 0) {
    return { maxY: 80_000, ticks: [0, 20_000, 40_000, 60_000, 80_000] };
  }

  const paddedMax = maxValue * 1.08;
  const step = Math.max(5000, Math.ceil(paddedMax / 4 / 5000) * 5000);
  const maxY = Math.ceil(paddedMax / step) * step;
  const ticks = Array.from({ length: 5 }, (_, index) => (maxY / 4) * index);

  return { maxY, ticks };
}

export default function EquitySimulatorGrowthChart({
  bars,
  className,
  onSelectBar,
}: EquitySimulatorGrowthChartProps) {
  const { t } = useTranslation();
  const chartHeight = 200;
  const safeBars = useMemo(
    () => (bars.length > 0 ? bars : [{ label: "—", value: 0 }]),
    [bars],
  );
  const [activeBarIndex, setActiveBarIndex] = useState(
    Math.max(0, safeBars.length - 1),
  );

  useEffect(() => {
    if (bars.length === 0) return;

    const lastIndex = bars.length - 1;
    setActiveBarIndex(lastIndex);
    onSelectBar?.(bars[lastIndex], lastIndex);
  }, [bars, onSelectBar]);

  const maxValue = Math.max(...safeBars.map((bar) => bar.value), 0);
  const { maxY, ticks } = buildYAxis(maxValue);

  const handleSelectBar = (bar: GrowthBarPoint, index: number) => {
    setActiveBarIndex(index);
    onSelectBar?.(bar, index);
  };

  return (
    <div className={cn("relative mt-2", className)}>
      <div className="flex gap-3">
        <div
          className="relative shrink-0 pt-1"
          style={{ width: 44, height: chartHeight }}
          aria-hidden
        >
          {ticks.map((tick) => {
            const bottom = maxY > 0 ? (tick / maxY) * 100 : 0;
            return (
              <span
                key={tick}
                className="absolute right-0 -translate-y-1/2 font-nexa text-[10px] leading-none text-white/45 sm:text-xs"
                style={{ bottom: `${bottom}%` }}
              >
                {formatAxisTick(tick)}
              </span>
            );
          })}
        </div>

        <div className="relative min-w-0 flex-1">
          <div className="relative" style={{ height: chartHeight }}>
            {ticks.map((tick) => {
              const bottom = maxY > 0 ? (tick / maxY) * 100 : 0;
              return (
                <div
                  key={`grid-${tick}`}
                  className="pointer-events-none absolute left-0 right-0 border-t border-dashed border-white/10"
                  style={{ bottom: `${bottom}%` }}
                  aria-hidden
                />
              );
            })}

            <div className="absolute inset-0 flex items-end justify-between gap-1.5 px-0.5 sm:gap-2">
              {safeBars.map((bar, index) => {
                const isHighlight = index === activeBarIndex;
                const columnHeight =
                  bar.value > 0
                    ? Math.max(8, (bar.value / maxY) * 100)
                    : 0;

                return (
                  <div
                    key={`${bar.label}-${index}`}
                    className="flex h-full flex-1 flex-col items-center justify-end"
                  >
                    <button
                      type="button"
                      aria-label={t("Balance at trade {{trade}}: {{value}}", {
                        trade: bar.label,
                        value: formatAxisTick(bar.value),
                      })}
                      aria-pressed={isHighlight}
                      onClick={() => handleSelectBar(bar, index)}
                      className={[
                        "w-full max-w-[34px] min-h-[8px] rounded-md border bg-gradient-to-t transition-[box-shadow,border-color,height] duration-300 ease-out",
                        isHighlight
                          ? "border-[#DBD633]/35 from-[#1CCDE6] via-[#9ED473] to-[#DBD633] shadow-[0_0_18px_rgba(28,205,230,0.42)]"
                          : "border-[#DBD633]/15 from-[#1CCDE6]/10 via-[#9ED473]/10 to-[#DBD633]/10 hover:border-[#DBD633]/25",
                      ].join(" ")}
                      style={{ height: `${columnHeight}%` }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-3 flex justify-between gap-1.5 px-0.5 sm:gap-2">
            {safeBars.map((bar, index) => (
              <span
                key={`${bar.label}-axis-${index}`}
                className={[
                  "flex-1 text-center font-nexa text-[10px] sm:text-xs",
                  index === activeBarIndex ? "text-white" : "text-white/45",
                ].join(" ")}
              >
                {bar.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
